import React, { useRef, useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPaperPlane, faTimes, faImage, faVideo, faTerminal, faBolt } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';

const SendMessage = ({
  inputText,
  setInputText,
  onSendMessage,
  selectedFile,
  setSelectedFile,
  filePreview,
  setFilePreview,
  isSending,
  commands = [],
  inputRef: externalInputRef
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const fileInputRef = useRef(null);
  const internalInputRef = useRef(null);
  const textInputRef = externalInputRef || internalInputRef;
  const suggestionsRef = useRef(null);

  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Normalizar los comandos recibidos para soportar tanto formato de BD (vComando/vTexto/vTitulo) como local (label/text)
  const normalizedCommands = (commands || []).map(cmd => ({
    command: cmd.vComando || cmd.label || '',
    title: cmd.vTitulo || cmd.title || cmd.label || '',
    text: cmd.vTexto || cmd.text || ''
  }));

  // Detectar trigger '/' cuando el usuario escribe
  useEffect(() => {
    if (!inputText) {
      setShowSuggestions(false);
      return;
    }

    // Busca si al final del texto hay un slash con posibles caracteres (ej: "/sal")
    const match = inputText.match(/(?:^|\s)\/([a-zA-Z0-9_-]*)$/);

    if (match !== null) {
      const query = match[1].toLowerCase();
      const matches = normalizedCommands.filter(cmd => {
        const cmdClean = cmd.command.replace(/^\//, '').toLowerCase();
        const titleClean = cmd.title.toLowerCase();
        return cmdClean.includes(query) || titleClean.includes(query);
      });

      if (matches.length > 0) {
        setFilteredCommands(matches);
        setSelectedIndex(0);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  }, [inputText, commands]);

  const applyCommand = cmd => {
    // Reemplaza el último "/query" en el texto por el contenido completo del comando
    const updated = inputText.replace(/(?:^|\s)\/([a-zA-Z0-9_-]*)$/, (match, p1) => {
      const startsWithSpace = match.startsWith(' ');
      return (startsWithSpace ? ' ' : '') + cmd.text;
    });

    setInputText(updated);
    setShowSuggestions(false);
    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus();
        const len = updated.length;
        textInputRef.current.setSelectionRange?.(len, len);
      }
    }, 20);
  };

  const handleKeyDown = e => {
    if (showSuggestions && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredCommands.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        applyCommand(filteredCommands[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
        return;
      }
    }

    // Al presionar Enter en el input (sin Shift), enviar el mensaje directamente
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setFilePreview({ url, type: 'image', name: file.name });
    } else if (file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setFilePreview({ url, type: 'video', name: file.name });
    } else {
      setFilePreview({ url: null, type: 'file', name: file.name });
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (filePreview?.url) {
      URL.revokeObjectURL(filePreview.url);
    }
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    textInputRef.current?.focus();
  };

  const handlePaste = e => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          setSelectedFile(file);
          const url = URL.createObjectURL(file);
          setFilePreview({ url, type: 'image', name: file.name || 'imagen_pegada.png' });
          break;
        }
      }
    }
  };

  const handleSubmit = e => {
    if (e) e.preventDefault();
    if (showSuggestions) {
      setShowSuggestions(false);
    }
    onSendMessage(e);
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 10);
  };

  return (
    <div
      className="border-top position-relative"
      style={{
        backgroundColor: isDark ? '#121e2d' : '#ffffff',
        borderColor: isDark ? '#233142' : '#e2e8f0'
      }}
    >
      {/* Popover / Menú flotante de Slash Commands al escribir / */}
      {showSuggestions && filteredCommands.length > 0 && (
        <div
          ref={suggestionsRef}
          className="position-absolute border rounded-3 shadow-lg p-1"
          style={{
            bottom: '100%',
            left: '16px',
            right: '16px',
            marginBottom: '8px',
            maxHeight: '230px',
            overflowY: 'auto',
            zIndex: 1050,
            backgroundColor: isDark ? '#182635' : '#ffffff',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          <div className="px-2 py-1 text-muted d-flex align-items-center justify-content-between border-bottom mb-1" style={{ fontSize: '0.72rem' }}>
            <span className="d-flex align-items-center gap-1 fw-semibold text-success">
              <FontAwesomeIcon icon={faBolt} style={{ fontSize: '0.7rem' }} /> Atajos disponibles
            </span>
            <span>Usa ↑ ↓ y pulsa <strong>Enter</strong> o <strong>Tab</strong> para elegir</span>
          </div>
          {filteredCommands.map((cmd, idx) => {
            const isSelected = idx === selectedIndex;
            return (
              <div
                key={cmd.command + idx}
                onClick={() => applyCommand(cmd)}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={`d-flex align-items-center justify-content-between p-2 rounded-2 cursor-pointer transition-all ${
                  isSelected ? 'bg-success text-white' : 'hover-bg-light text-dark'
                }`}
                style={{
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#065f46' : 'transparent'
                }}
              >
                <div className="d-flex align-items-center gap-2 overflow-hidden">
                  <span
                    className={`badge ${isSelected ? 'bg-white text-dark' : 'bg-light text-success border'}`}
                    style={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    {cmd.command.startsWith('/') ? cmd.command : `/${cmd.command}`}
                  </span>
                  <div className="d-flex flex-column text-truncate">
                    <span className={`fw-semibold ${isSelected ? 'text-white' : 'text-dark'}`} style={{ fontSize: '0.8rem' }}>
                      {cmd.title}
                    </span>
                    <small
                      className={`text-truncate ${isSelected ? 'text-white-50' : 'text-muted'}`}
                      style={{ fontSize: '0.73rem', maxWidth: '420px' }}
                    >
                      {cmd.text}
                    </small>
                  </div>
                </div>
                {isSelected && (
                  <span className="badge bg-white bg-opacity-25 text-white" style={{ fontSize: '0.68rem' }}>
                    Enter ↵
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* File Preview Bar */}
      {filePreview && (
        <div
          className="p-2 px-3 border-bottom d-flex align-items-center justify-content-between"
          style={{
            backgroundColor: isDark ? '#182635' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          <div className="d-flex align-items-center gap-2 overflow-hidden">
            {filePreview.type === 'image' && filePreview.url ? (
              <img
                src={filePreview.url}
                alt="Preview"
                style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '6px' }}
              />
            ) : filePreview.type === 'video' ? (
              <div className="bg-dark text-white rounded d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px' }}>
                <FontAwesomeIcon icon={faVideo} />
              </div>
            ) : (
              <div className="bg-secondary text-white rounded d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px' }}>
                <FontAwesomeIcon icon={faImage} />
              </div>
            )}
            <div className="d-flex flex-column">
              <span className="fw-semibold text-truncate" style={{ fontSize: '0.82rem', maxWidth: '300px' }}>
                {filePreview.name}
              </span>
              <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                {filePreview.type === 'image' ? 'Foto lista para enviar' : 'Video/Archivo listo'}
              </small>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-link text-danger p-1"
            onClick={handleRemoveFile}
            title="Quitar archivo"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="p-3 d-flex align-items-center gap-2">
        <input
          type="file"
          ref={fileInputRef}
          hidden
          accept="image/*,video/*,application/pdf"
          onChange={handleFileChange}
        />

        <input
          ref={textInputRef}
          type="text"
          className="form-control rounded-pill px-3 py-2"
          style={{
            backgroundColor: isDark ? '#182635' : '#f1f5f9',
            color: isDark ? '#f9fafd' : '#0f172a',
            border: isDark ? '1px solid #233142' : 'none',
            fontSize: '0.88rem'
          }}
          placeholder={selectedFile ? 'Escribe un pie de foto (opcional)...' : 'Escribe un mensaje o escribe "/" para atajos rápidos...'}
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          autoFocus
        />

        {/* Botón Agregar (+) al lado del botón de enviar */}
        <button
          type="button"
          className="btn d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-none"
          style={{
            fontSize: '0.84rem',
            fontWeight: 500,
            color: isDark ? '#e2e8f0' : '#334155',
            backgroundColor: isDark ? '#182635' : '#f8fafc',
            border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
            whiteSpace: 'nowrap'
          }}
          title="Agregar foto, video o archivo"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSending}
        >
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.85rem', color: isDark ? '#34d399' : '#065f46' }} />
          <span>Agregar</span>
        </button>

        {/* Botón Enviar */}
        <button
          type="submit"
          className="btn rounded-circle d-flex align-items-center justify-content-center border-0 text-white flex-shrink-0"
          style={{ width: '40px', height: '40px', backgroundColor: '#065f46' }}
          title="Enviar mensaje"
          disabled={isSending || (!inputText.trim() && !selectedFile)}
        >
          {isSending ? (
            <div className="spinner-border spinner-border-sm text-white" role="status"></div>
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '0.9rem' }} />
          )}
        </button>
      </form>
    </div>
  );
};

export default SendMessage;

